import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { UploadCloud, Copy, Trash2, Loader2, Check, X } from "lucide-react";

export default function MediaLibrary() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editAlt, setEditAlt] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/media/list");
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const uploadOne = async (file) => {
    if (!file.type?.startsWith("image/")) return;
    if (file.size > 5 * 1024 * 1024) { toast.error(`${file.name}: too large (max 5 MB)`); return; }
    const form = new FormData();
    form.append("file", file);
    try {
      await api.post("/media/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
    } catch (e) { toast.error(`${file.name}: ${formatApiError(e)}`); }
  };

  const uploadMany = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(files.length);
    let done = 0;
    await Promise.all(Array.from(files).map(async (f) => {
      await uploadOne(f);
      done += 1;
      setUploading(files.length - done);
    }));
    toast.success(`Uploaded ${files.length} file${files.length > 1 ? "s" : ""}`);
    setUploading(0);
    load();
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    uploadMany(e.dataTransfer.files);
  };

  const onFilePick = (e) => {
    uploadMany(e.target.files);
    e.target.value = "";
  };

  const copy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("URL copied");
    } catch { toast.error("Copy failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this file?")) return;
    try {
      await api.delete(`/media/${id}`); toast.success("Deleted"); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveAlt = async () => {
    if (!editing) return;
    try {
      await api.patch(`/media/${editing.id}`, { alt_text: editAlt });
      toast.success("Alt text saved");
      setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs tracking-widest uppercase text-slate-500">Assets</div>
          <h1 className="font-display text-3xl text-[#0B132B]">Media Library</h1>
          <p className="text-sm text-slate-500 mt-1">Every uploaded image is stored here — reuse across sections.</p>
        </div>
        <label className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#0B132B] text-white text-sm cursor-pointer">
          <UploadCloud size={14} /> Add files
          <input type="file" multiple accept="image/*" onChange={onFilePick} className="hidden" />
        </label>
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`mt-6 rounded-2xl border-2 border-dashed transition-colors p-8 text-center ${
          dragOver ? "border-[#0B132B] bg-slate-100" : "border-slate-200 bg-white"
        }`}
      >
        {uploading > 0 ? (
          <div className="inline-flex items-center gap-2 text-sm text-slate-600">
            <Loader2 size={16} className="animate-spin" /> Uploading… {uploading} remaining
          </div>
        ) : (
          <div className="text-sm text-slate-500">
            <UploadCloud size={22} className="mx-auto text-slate-400" />
            <div className="mt-2">Drop images here to upload — jpg, png, webp, gif · up to 5 MB each</div>
          </div>
        )}
      </div>

      <div className="mt-8">
        {loading ? (
          <div className="text-slate-500 text-sm">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-slate-500 text-sm">No files yet — drop some above.</div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {items.map((m) => (
              <div key={m.id} className="rounded-2xl bg-white ring-1 ring-slate-200 overflow-hidden group">
                <div className="relative aspect-[4/3] bg-slate-100">
                  <img src={m.url} alt={m.alt_text || m.original_filename} className="absolute inset-0 w-full h-full object-cover" />
                  {m.preset && (
                    <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-white/90 backdrop-blur text-[10px] uppercase tracking-widest text-[#0B132B]">{m.preset}</span>
                  )}
                </div>
                <div className="p-3">
                  <div className="text-xs font-medium text-[#0B132B] truncate" title={m.original_filename}>{m.original_filename || "Untitled"}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5 truncate" title={m.alt_text}>{m.alt_text || <span className="italic">no alt text</span>}</div>
                  <div className="mt-2 flex gap-1">
                    <button onClick={() => copy(m.url)} className="flex-1 h-8 rounded-lg ring-1 ring-slate-200 text-xs inline-flex items-center justify-center gap-1 hover:bg-slate-50" title="Copy URL"><Copy size={12} /> Copy</button>
                    <button onClick={() => { setEditing(m); setEditAlt(m.alt_text || ""); }} className="h-8 px-3 rounded-lg ring-1 ring-slate-200 text-xs hover:bg-slate-50" title="Edit alt text">Alt</button>
                    <button onClick={() => remove(m.id)} className="h-8 w-8 grid place-items-center rounded-lg ring-1 ring-red-200 text-red-600 hover:bg-red-50" title="Delete"><Trash2 size={12} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-[#0B132B]/60 backdrop-blur grid place-items-center p-4" onClick={() => setEditing(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl p-6 sm:p-8 w-full max-w-md shadow-high">
            <div className="flex justify-between items-start">
              <div>
                <div className="text-xs tracking-widest uppercase text-slate-500">Alt Text</div>
                <div className="font-display text-xl text-[#0B132B] truncate max-w-xs">{editing.original_filename}</div>
              </div>
              <button onClick={() => setEditing(null)} className="text-slate-500"><X size={18} /></button>
            </div>
            <img src={editing.url} alt="" className="mt-4 w-full h-40 object-cover rounded-xl ring-1 ring-slate-200" />
            <label className="block mt-5">
              <span className="text-[11px] uppercase tracking-widest text-slate-500">Describe this image</span>
              <input value={editAlt} onChange={(e) => setEditAlt(e.target.value)} autoFocus
                     placeholder="e.g. Shubhi Jain speaking at a career session"
                     className="mt-1.5 w-full h-11 px-4 rounded-xl bg-white ring-1 ring-slate-200 focus:ring-[#0B132B] focus:outline-none text-sm" />
            </label>
            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="h-10 px-4 rounded-lg ring-1 ring-slate-200 text-sm">Cancel</button>
              <button onClick={saveAlt} className="h-10 px-4 rounded-lg bg-[#0B132B] text-white text-sm inline-flex items-center gap-2"><Check size={14} /> Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
